import React, { useRef, useEffect, useState } from 'react';
import './Conversation.scss';
import DOMPurify from 'dompurify';
import Linkify from 'linkify-react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import ChatUserAvatar from './ChatUserAvatar';
import AttachmentPicker, { AttachmentFile } from './AttachmentPicker';
import FirstMessageSuggestions from './FirstMessageSuggestions';
import MessageAttachments from './MessageAttachments';
import { getCookie } from '../utils/utils';
import LoadingSpinner from '../common/LoadingSpinner';
import { OffMarketWillingnessEvent as OffMarketWillingnessEventType } from '../utils/types';
import { ListingCard, BuyerCard } from '../ListingCard';
import type { ListingCardListing } from '../ListingCard';
import StackedAvatars from './StackedAvatars';
import ParticipantPanel from './ParticipantPanel';
import InviteParticipantModal from './InviteParticipantModal';
import { formatCardPrice } from '../ListingCard/cardUtils';
import { listingPath } from '../utils/listingSlug';
import { useOpenListing } from '../utils/useOpenListing';

/**
 * Resolve the viewer's marketplace role (BUYER/SELLER) from backend-provided signals.
 *
 * Prefer `currentUserSide` — the backend computes this from marketplace roles for
 * originals and from participant_sides for invitees, so it is correct in multi-party
 * chats. Fall back to the legacy 2-party inference (`otherUserRole` flipped) only for
 * pre-multi-party conversations that may not carry a side.
 */
export function resolveViewerMarketplaceRole(
  currentUserSide: 'buyer' | 'seller' | null | undefined,
  otherUserRole: 'BUYER' | 'SELLER' | 'UNKNOWN'
): 'BUYER' | 'SELLER' | 'UNKNOWN' {
  if (currentUserSide === 'seller') return 'SELLER';
  if (currentUserSide === 'buyer') return 'BUYER';
  if (otherUserRole === 'BUYER') return 'SELLER';
  if (otherUserRole === 'SELLER') return 'BUYER';
  return 'UNKNOWN';
}

type TipData = {
  type: 'SELL_LISTING_TIP';
  sell_willingness_id: string;
  building_id: string;
  address: any;
  address_string: string;
  property_description?: string;
  property_size?: number;
  property_rooms?: number;
  property_floor?: number;
  property_has_elevator?: boolean;
  property_has_balcony?: boolean;
  property_renovation_status?: string;
  property_asking_price?: number;
  images?: any[];
};

type ConversationProps = {
  conversationId: string;
  conversation: {
    id: string;
    otherUser: {
      name: string;
      avatar: string;
      verified?: boolean;
    };
    buildingInfo: {
      name: string;
      address: string;
    };
    isEnded?: boolean;
    endedAt?: Date;
    isJaakkoConversation?: boolean;
    participants?: Array<{
      userId: string;
      name: string;
      avatar: string | null;
      role: string;
      marketplaceRole?: 'BUYER' | 'SELLER' | null;
      roleLabel?: string;
      isOriginal?: boolean;
      isSelf: boolean;
      hasSentMessage?: boolean;
      verified?: boolean;
    }>;
    currentUserRole?: string;
    canInvite?: boolean;
    pendingInviteEmail?: string | null;
  };
  messages: {
    id: string;
    sender: 'self' | 'other' | 'jaakko' | 'system';
    text: string;
    timestamp: Date;
    attachments?: any[];
    tipData?: TipData;
    senderId?: string;
    senderName?: string;
    /**
     * Own messages only: true once the other side has opened the chat since
     * it was sent. Missing when the server does not say (group chat, older
     * server), in which case no status shows (JAA-258).
     */
    read?: boolean;
    systemEvent?: {
      type: string;
      actorLabel?: { type: string; name?: string };
      targetLabel?: { type: string; name?: string };
      newRole?: string;
      role?: string;
    };
  }[];
  isLoading: boolean;
  newMessage: string;
  otherUserRole: 'BUYER' | 'SELLER' | 'UNKNOWN';
  setNewMessage: (message: string) => void;
  sendMessage: (files?: File[]) => Promise<boolean>;
  goBack: () => void;
  offMarketSellWillingnessId?: string;
  offMarketBuyWillingnessId?: string;
  currentUserSide?: 'buyer' | 'seller' | null;
  hasViewedWelcome?: boolean;
  refreshConversations?: () => void;
  onRefreshMessages?: (invalidateReadStatus?: boolean) => void;
  isJaakkoConversation?: boolean;
  showParticipantPanel: boolean;
  setShowParticipantPanel: (show: boolean) => void;
  showInviteModal: boolean;
  setShowInviteModal: (show: boolean) => void;
  onEndChat?: () => void;
  onShowTemplate?: () => void;
  onShowExpertHelp?: () => void;
  /** Passed only for a signed-out draft chat: Send calls it instead of sending (JAA-325). */
  onUnauthenticatedSend?: () => void;
  /** A one-line status shown above the composer (the draft's "not sent yet" after login, JAA-325). */
  composerHint?: string;
};

// Rows the backend writes when a conversation opens ("interested", "started");
// nobody typed them, so they never count as a party having written.
const isConversationPlaceholder = (text: string): boolean =>
  text === 'INTERESTED_BUYER' || text === 'INTERESTED_SELLER' || text === 'Conversation started';

/**
 * Picks the one message that shows Sent/Seen (JAA-258): the viewer's own
 * message, when it is the latest real message of a 1:1 chat. One status is
 * enough because the server marks everything read at once, and a reply from
 * the other side already says the message was seen. Returns null for group
 * chats (a `read` there cannot say who opened it), when the latest message
 * is not the viewer's, and when the server sent no `read` (older server).
 */
export const latestOwnMessageStatus = (
  messages: { id: string; sender: string; read?: boolean }[],
  participantCount: number,
): { messageId: string; seen: boolean } | null => {
  if (participantCount !== 2) return null;
  const latest = [...messages].reverse().find((message) => message.sender !== 'system');
  if (!latest || latest.sender !== 'self' || latest.read === undefined) return null;
  return { messageId: latest.id, seen: latest.read };
};

/**
 * Longest chat text the server stores (jaakkocom MAX_MESSAGE_TEXT_LENGTH,
 * JAA-438). The textarea has no native maxLength: it counts UTF-16 units (an
 * emoji twice) and silently cuts a paste. A text over it, typed, pasted or
 * restored after login, is caught by charsOverMessageLimit instead.
 */
const MAX_MESSAGE_LENGTH = 5000;

/**
 * How many characters `text` is over MAX_MESSAGE_LENGTH, 0 when it fits. Counts
 * code points of the raw text, as the server does: never below the server's
 * count, whether it counts raw (a thread send) or trimmed (the first message).
 * The length check first keeps the common short text from being spread.
 */
export const charsOverMessageLimit = (text: string): number =>
  text.length > MAX_MESSAGE_LENGTH
    ? Math.max(0, Array.from(text).length - MAX_MESSAGE_LENGTH)
    : 0;

/**
 * The "too long" copy: the limit as a number in the viewer's language (5,000 in
 * English, 5 000 in Finnish) and how many characters to remove. Shared by the
 * composer notice and the send refusals so the limit has one source.
 */
export const messageTooLongText = (
  t: TFunction,
  language: string,
  over: number,
): string =>
  t('messages.tooLong', {
    count: over,
    max: new Intl.NumberFormat(language || 'en-GB').format(MAX_MESSAGE_LENGTH),
  });

/**
 * One chat thread: header with its menus, the message list and the composer.
 * Also renders the unsent draft chat (no conversation id yet), which is
 * signed out exactly when the parent passes `onUnauthenticatedSend` (JAA-325).
 * A text over MAX_MESSAGE_LENGTH, the server's limit for both send routes
 * (JAA-438), whether typed, pasted or restored after login, shows how many
 * characters to remove and keeps Send off until it fits; nothing is cut.
 * In a 1:1 chat the viewer's latest message shows Sent or Seen next to its
 * time (JAA-258).
 */
const Conversation: React.FC<ConversationProps> = ({
  conversationId,
  conversation,
  messages,
  isLoading,
  newMessage,
  setNewMessage,
  sendMessage,
  goBack,
  otherUserRole,
  offMarketSellWillingnessId,
  offMarketBuyWillingnessId,
  currentUserSide,
  hasViewedWelcome,
  refreshConversations,
  onRefreshMessages,
  isJaakkoConversation,
  showParticipantPanel,
  setShowParticipantPanel,
  showInviteModal,
  setShowInviteModal,
  onEndChat,
  onShowTemplate,
  onShowExpertHelp,
  onUnauthenticatedSend,
  composerHint,
}) => {
  const { t, i18n } = useTranslation();
  const openListing = useOpenListing();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const thisUserHasSentMessage = messages.some(
    (message) => message.sender === 'self' && !isConversationPlaceholder(message.text),
  );
  // Any real message from the other side, loaded with the thread.
  const otherPartyHasWritten = messages.some(
    (message) => message.sender === 'other' && !isConversationPlaceholder(message.text),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [otherPartyWillingness, setOtherPartyWillingness] = useState<OffMarketWillingnessEventType | null>(null);
  const [isLoadingWillingness, setIsLoadingWillingness] = useState(false);
  const previousMessagesLength = useRef(0);
  const hasMarkedAsViewed = useRef(false);
  const participants = conversation.participants || [];
  const currentUserParticipantRole = conversation.currentUserRole || 'member';
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const optionsMenuRef = useRef<HTMLDivElement>(null);
  const helpMenuRef = useRef<HTMLDivElement>(null);

  // Viewer's marketplace side (BUYER/SELLER). NOTE: this is NOT the chat-membership
  // role from `conversation.currentUserRole` (which is 'member') — it's the side of
  // the deal the viewer is on, resolved via the helper above.
  const currentUserRole = resolveViewerMarketplaceRole(currentUserSide, otherUserRole);

  // Select which willingness ID to fetch based on current user's side.
  // A seller (original OR seller-side invitee) sees the buyer's buy listing;
  // a buyer (original OR buyer-side invitee) sees the seller's sell listing.
  const willingnessIdToFetch = currentUserRole === 'SELLER' ? offMarketBuyWillingnessId : offMarketSellWillingnessId;
  // Only a signed-out draft gets the send callback, so a real conversation never reads as signed out (JAA-325).
  const isSignedOutDraft = !!onUnauthenticatedSend;
  // Over the limit for typed, pasted or restored text alike; the notice shows and Send stays off until it fits.
  const overLimitBy = charsOverMessageLimit(newMessage);

  // Fetch opposite party's off-market willingness details. The endpoint needs
  // a login, so a signed-out draft skips it and loads the card after login.
  useEffect(() => {
    if (!willingnessIdToFetch || isSignedOutDraft) {
      setOtherPartyWillingness(null);
      return;
    }

    const fetchWillingness = async () => {
      setIsLoadingWillingness(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/off-market-willingness/${willingnessIdToFetch}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        if (!response.ok) {
          throw new Error('Failed to fetch willingness');
        }
        const data = await response.json();
        // Transform to OffMarketWillingnessEventType
        const willingnessEvent: OffMarketWillingnessEventType = {
          _id: data._id,
          isUserOwnEvent: false, // In chat context, we don't show edit/delete options
          buildingId: data.building_id,
          address: data.address,
          updated_at: data.updated_at || new Date().toISOString(),
          last_modified_value: data.could_sell ? 'COULD_SELL' : 'COULD_BUY',
          could_buy: data.could_buy ?? false,
          could_sell: data.could_sell ?? false,
          images: data.images,
          property_description: data.property_description,
          property_floor: data.property_floor,
          property_has_balcony: data.property_has_balcony,
          property_has_elevator: data.property_has_elevator,
          property_renovation_status: data.property_renovation_status,
          property_rooms: data.property_rooms,
          property_size: data.property_size,
          property_asking_price: data.property_asking_price,
        };
        setOtherPartyWillingness(willingnessEvent);
      } catch (error) {
        console.error('Error fetching willingness:', error);
        setOtherPartyWillingness(null);
      } finally {
        setIsLoadingWillingness(false);
      }
    };

    fetchWillingness();
  }, [willingnessIdToFetch, isSignedOutDraft]);

  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkScreenSize();

    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setShowOptionsMenu(false);
      }
      if (helpMenuRef.current && !helpMenuRef.current.contains(event.target as Node)) {
        setShowHelpMenu(false);
      }
    }

    if (showOptionsMenu || showHelpMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptionsMenu, showHelpMenu]);

  const scrollToBottom = (smooth: boolean) => {
    if (!messagesEndRef.current) return;
    // Use requestAnimationFrame to ensure layout has settled
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    });
  };

  const scheduleScrollToBottom = (smoothFirst: boolean) => {
    scrollToBottom(smoothFirst);

    // Attempt again shortly after to account for async image/video layout
    const t1 = setTimeout(() => scrollToBottom(false), 80);
    const t2 = setTimeout(() => scrollToBottom(false), 300);
    const t3 = setTimeout(() => scrollToBottom(false), 800);

    // Also listen for image/video load events to adjust after media renders
    // Only listen to images/videos in actual messages, NOT in the willingness event carousel
    // to avoid scrolling when user browses carousel images
    const container = messagesContainerRef.current;
    let imgs: HTMLImageElement[] = [];
    let videos: HTMLVideoElement[] = [];
    let handleMediaLoad: (() => void) | null = null;

    if (container) {
      handleMediaLoad = () => scrollToBottom(false);
      // Only select images/videos within message elements, excluding the willingness event
      imgs = Array.from(container.querySelectorAll('.conversation__message img')) as HTMLImageElement[];
      imgs.forEach(img => {
        if (!img.complete) img.addEventListener('load', handleMediaLoad!, { once: true });
      });
      videos = Array.from(container.querySelectorAll('.conversation__message video')) as HTMLVideoElement[];
      videos.forEach(v => v.addEventListener('loadedmetadata', handleMediaLoad!, { once: true }));
    }

    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (handleMediaLoad) {
        imgs.forEach(img => img.removeEventListener('load', handleMediaLoad!));
        videos.forEach(v => v.removeEventListener('loadedmetadata', handleMediaLoad!));
      }
    };
  };

  // Scroll to bottom on new messages, once the welcome was viewed or the user has written here themselves
  useEffect(() => {
    if (hasViewedWelcome || thisUserHasSentMessage) {
      // Only scroll if new messages were added
      if (messages.length > previousMessagesLength.current) {
        previousMessagesLength.current = messages.length;
        return scheduleScrollToBottom(true);
      }
      previousMessagesLength.current = messages.length;
    } else {
      // First time viewing - don't scroll, just track length
      previousMessagesLength.current = messages.length;
    }
  }, [messages, hasViewedWelcome, thisUserHasSentMessage]);

  // Mark welcome as viewed after user has seen it (first time only)
  useEffect(() => {
    if (!conversationId || hasViewedWelcome || hasMarkedAsViewed.current) {
      return;
    }

    // Mark as viewed after a short delay to ensure user has seen the welcome
    const timer = setTimeout(async () => {
      if (hasMarkedAsViewed.current) return;
      hasMarkedAsViewed.current = true;

      try {
        await fetch(
          `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/mark-welcome-viewed`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': getCookie('csrf_access_token'),
            },
          }
        );
        // Refresh conversations to update the hasViewedWelcome flag
        refreshConversations?.();
      } catch (error) {
        console.error('Error marking welcome as viewed:', error);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [conversationId, hasViewedWelcome, refreshConversations]);

  // Reset tracking ref when conversation changes
  useEffect(() => {
    hasMarkedAsViewed.current = false;
    previousMessagesLength.current = 0;
  }, [conversationId]);

  // Handle send on Ctrl+Enter (or Cmd+Enter on Mac)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle paste event for images
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      handleAddAttachments(files);
    }
  };

  /**
   * Queue files for the next send. Every add path (file input, drop, paste)
   * goes through here, and attachments are signed-in only, so a signed-out
   * draft ignores them (JAA-325).
   */
  const handleAddAttachments = (files: File[]) => {
    if (isSignedOutDraft) return;
    const newAttachments: AttachmentFile[] = files.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  // Handle removing attachment
  const handleRemoveAttachment = (id: string) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment?.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter(a => a.id !== id);
    });
  };

  // Upload attachments to server
  const uploadAttachments = async (): Promise<any[]> => {
    if (attachments.length === 0) return [];
    if (!conversationId) {
      throw new Error('Cannot upload attachments without a conversation');
    }

    const formData = new FormData();
    attachments.forEach(att => {
      formData.append('files', att.file);
    });

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/attachments`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: formData
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload attachments');
    }

    const data = await response.json();
    return data.attachments;
  };

  /**
   * Send the typed text and any queued files. Refuses an over-limit text before
   * anything else (the composer shows why), so no queued file is uploaded and
   * cleared for a text the server would reject. A signed-out draft hands off to
   * login instead of sending (JAA-325).
   */
  const handleSendMessage = async () => {
    if (overLimitBy > 0) return;
    if (!newMessage.trim() && attachments.length === 0) return;
    if (isUploading) return;
    if (isSignedOutDraft) {
      onUnauthenticatedSend?.();
      return;
    }

    setIsUploading(true);
    try {
      // A draft has no conversation-scoped endpoints. Hand its raw files to
      // the parent so the start request can create the conversation and upload
      // everything atomically beneath the reserved server-side ID.
      if (!conversationId) {
        if (attachments.length > 0) {
          setAttachments(prev => prev.map(a => ({
            ...a,
            uploading: true,
            uploadProgress: 0,
            error: undefined,
          })));
        }
        const sent = await sendMessage(attachments.map(att => att.file));
        if (!sent) {
          setAttachments(prev => prev.map(a => ({
            ...a,
            uploading: false,
            error: 'Upload failed',
          })));
          return;
        }
        attachments.forEach(att => {
          if (att.preview) {
            URL.revokeObjectURL(att.preview);
          }
        });
        setAttachments([]);
        return;
      }

      let uploadedAttachments: any[] = [];

      // Upload attachments first if any
      if (attachments.length > 0) {
        // Update attachment states to show uploading
        setAttachments(prev => prev.map(a => ({ ...a, uploading: true, uploadProgress: 0, error: undefined })));

        uploadedAttachments = await uploadAttachments();

        // Clear attachments after successful upload
        attachments.forEach(att => {
          if (att.preview) {
            URL.revokeObjectURL(att.preview);
          }
        });
        setAttachments([]);
      }

      // Send message with attachments
      await sendMessageWithAttachments(uploadedAttachments);
    } catch (error) {
      console.error('Error sending message:', error);
      // Update attachment states to show error
      setAttachments(prev => prev.map(a => ({
        ...a,
        uploading: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      })));
    } finally {
      setIsUploading(false);
    }
  };

  // Send message with attachments
  const sendMessageWithAttachments = async (uploadedAttachments: any[]) => {
    const messageData: any = {};

    if (newMessage.trim()) {
      messageData.text = newMessage;
    }

    if (uploadedAttachments.length > 0) {
      messageData.attachments = uploadedAttachments;
    }

    await sendMessageWithData(messageData);
  };

  // Modified sendMessage to handle attachments
  const sendMessageWithData = async (data: any) => {
    // If there's only text and no attachments, use the original sendMessage
    if (!data.attachments || data.attachments.length === 0) {
      await sendMessage();
      return;
    }
    if (!conversationId) {
      throw new Error('Cannot send to an empty conversation');
    }

    // New behavior with attachments
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(data)
      }
    );

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    setNewMessage('');
    onRefreshMessages?.(true);
  };

  // Format timestamp to display time in i18n language format
  const formatMessageTime = (timestamp: Date) => {
    // Check if timestamp is valid
    if (!timestamp || isNaN(timestamp.getTime())) {
      console.warn('Invalid timestamp detected:', timestamp);
      return '';
    }

    const userLocale = i18n.language || 'en-GB';
    return new Intl.DateTimeFormat(userLocale, {
      hour: '2-digit',
      minute: '2-digit'
    }).format(timestamp);
  };

  // Filter out the first message and handle the case when there's only one message
  // For Jaakko conversations, show all messages (no system "Conversation started" message to skip)
  const displayMessages = isJaakkoConversation
    ? messages
    : (messages.length <= 1 ? [] : messages.slice(1));

  const readStatus = isJaakkoConversation
    ? null
    : latestOwnMessageStatus(displayMessages, participants.length);

  // Group messages by date (only for messages we'll display)
  const groupedMessages = displayMessages.reduce((groups: any, message) => {
    // Ensure timestamp is valid
    if (!message.timestamp || isNaN(new Date(message.timestamp).getTime())) {
      console.warn('Invalid message timestamp detected:', message);
      return groups;
    }

    // Use ISO date format (YYYY-MM-DD) for consistent, parseable keys
    const date = new Date(message.timestamp).toISOString().split('T')[0];
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  // Format date for date separators based on i18n language
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string detected:', dateStr);
      return '';
    }

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Get locale from i18n
    const userLocale = i18n.language || 'en-GB';

    if (date.toDateString() === today.toDateString()) {
      return t('messages.today');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('messages.yesterday');
    } else {
      return new Intl.DateTimeFormat(userLocale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      }).format(date);
    }
  };

  // Format the ended date if available
  const formatEndedDate = (date?: Date) => {
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    const userLocale = i18n.language || 'en-GB';
    return new Intl.DateTimeFormat(userLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Short form of the building address for i18n interpolation in the suggestion
  // bubbles. Strips the apartment suffix (e.g. " A 5") and the postal code to
  // yield "<street>, <city>". Null when no address is available.
  const _fullAddr = conversation?.buildingInfo?.address || null;
  let shortAddress: string | null = null;
  if (_fullAddr) {
    const parts = _fullAddr.split(',').map((s: string) => s.trim());
    const street = parts[0]?.replace(/\s+[A-Z]\s*\d+$/, '').trim() || '';
    const city = (parts[1] || '').replace(/^\d{5}\s+/, '').trim();
    shortAddress = street && city ? `${street}, ${city}` : street || null;
  }

  const otherPartyListing = otherPartyWillingness
    ? ({
        ...otherPartyWillingness,
        building_id: otherPartyWillingness.buildingId,
      } as unknown as ListingCardListing)
    : null;

  return (
    <div className="conversation">
      <div className="conversation__header">
        <div className="conversation__header-top">
          <button className="conversation__back" onClick={goBack}>
            <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" focusable="false" style={{ display: 'block', fill: 'none', height: '16px', width: '16px', stroke: 'currentcolor', strokeWidth: 4, overflow: 'visible' }}>
              <path d="m20 28-11.29289322-11.2928932c-.39052429-.3905243-.39052429-1.0236893 0-1.4142136l11.29289322-11.2928932"></path>
            </svg>
          </button>
          <div className="conversation__user">
            <div className="conversation__user-avatar">
              {isJaakkoConversation ? (
                <img src="/assets/jaakko_head.png" alt="Jaakko" style={{ width: 40, height: 40, borderRadius: '50%' }} />
              ) : (
                <StackedAvatars
                  participants={participants.filter(p => !p.isSelf)}
                  maxVisible={3}
                  size={40}
                />
              )}
            </div>
            <div className="conversation__user-info">
              <h3>
                {isJaakkoConversation
                  ? 'Jaakko'
                  : participants.filter(p => !p.isSelf).map(p => {
                      if (!p.name) {
                        switch (p.roleLabel) {
                          case 'seller':
                            return t('messages.seller');
                          case 'buyer':
                            return t('messages.buyer');
                          case 'seller_participant':
                            return t('messages.sellerParticipant');
                          case 'buyer_participant':
                            return t('messages.buyerParticipant');
                          default:
                            return t('messages.participants.participant');
                        }
                      }
                      return p.name;
                    }).join(', ')
                }
              </h3>
              <p>{conversation.buildingInfo.address}</p>
            </div>
          </div>
        </div>
        {!isJaakkoConversation && (
          <div className="conversation__header-actions">
            {conversationId && (
            <div className="conversation__options-container" ref={optionsMenuRef}>
              <button
                className="conversation__action-button"
                onClick={() => { setShowOptionsMenu(!showOptionsMenu); setShowHelpMenu(false); }}
              >
                <Trans i18nKey="messages.options">Options</Trans>
              </button>
              {showOptionsMenu && (() => {
                const canAdd = conversation.canInvite && !conversation.isEnded;
                const selfParticipant = participants.find(p => p.isSelf);
                const canEnd = !conversation.isEnded && !!selfParticipant?.isOriginal;
                return (
                  <div className="conversation__options-menu">
                    <button
                      className="conversation__menu-item"
                      onClick={() => { setShowOptionsMenu(false); setShowParticipantPanel(true); }}
                    >
                      <Trans i18nKey="messages.participants.title">Members</Trans>
                    </button>
                    {canAdd && (
                      <button
                        className="conversation__menu-item"
                        onClick={() => { setShowOptionsMenu(false); setShowInviteModal(true); }}
                      >
                        <Trans i18nKey="messages.participants.addPeople">Add people</Trans>
                      </button>
                    )}
                    {canEnd && (
                      <button
                        className="conversation__menu-item conversation__menu-item--warning"
                        onClick={() => { setShowOptionsMenu(false); onEndChat?.(); }}
                      >
                        <Trans i18nKey="messages.endChat">End chat</Trans>
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            )}
            {!conversation.isEnded && (
              <div className="conversation__help-container" ref={helpMenuRef}>
                <button
                  className="conversation__action-button"
                  onClick={() => { setShowHelpMenu(!showHelpMenu); setShowOptionsMenu(false); }}
                >
                  {currentUserRole === 'SELLER'
                    ? <Trans i18nKey="messages.helpWithSelling">Help with selling</Trans>
                    : <Trans i18nKey="messages.helpWithBuying">Help with buying</Trans>
                  }
                </button>
                {showHelpMenu && (
                  <div className="conversation__help-menu">
                    <button
                      className="conversation__menu-item"
                      onClick={() => {
                        setShowHelpMenu(false);
                        window.open(t('messages.selfSellingGuideUrl'), '_blank', 'noopener');
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="m17.5 11c-3.584 0-6.5 2.916-6.5 6.5s2.916 6.5 6.5 6.5 6.5-2.916 6.5-6.5-2.916-6.5-6.5-6.5zm1 10c0 .552-.447 1-1 1s-1-.448-1-1v-3.5c0-.552.447-1 1-1s1 .448 1 1zm-1-6c-.552 0-1-.448-1-1s.448-1 1-1 1 .448 1 1-.448 1-1 1z" />
                        <path d="m17.98.11c-2.59-.38-4.88.24-6.98 1.89-2.1-1.65-4.39-2.27-6.98-1.89-.87.12-1.52.87-1.52 1.73v10.97c0 .49.21.96.58 1.29.37.34.88.5 1.38.44 1.59-.18 3.13.15 4.55.96.07.03.13.07.2.11.01-.04.02-.07.03-.11.11-.46.26-.91.45-1.34-1.37-.77-2.85-1.17-4.38-1.17-.34 0-.67.02-1.02.06-.11.01-.18-.04-.21-.07-.05-.04-.08-.1-.08-.17v-10.97c0-.12.1-.23.23-.25 2.26-.33 4.18.23 6.02 1.74.09.06.17.14.26.21.28.25.7.25.98 0 .09-.07.17-.15.26-.21 1.84-1.51 3.76-2.06 6.02-1.74.13.02.23.13.23.25v7.17c.51.03 1.01.11 1.5.23v-7.4c0-.86-.65-1.61-1.52-1.73z" />
                        <path d="m9.24 15.5c-.01.04-.02.07-.03.11-.1.45-.17.92-.2 1.39h-7.26c-.96 0-1.75-.79-1.75-1.75v-11.5c0-.96.78-1.74 1.74-1.75h1.26c.41 0 .75.33.75.75 0 .41-.33.75-.75.75h-1.25c-.14 0-.25.12-.25.25v11.5c0 .14.11.25.25.25z" />
                        <path d="m22 3.75v6.54c-.47-.29-.97-.54-1.5-.74v-5.8c0-.14-.11-.25-.25-.25h-1.25c-.41 0-.75-.34-.75-.75s.34-.75.75-.75h1.25c.96 0 1.75.79 1.75 1.75z" />
                        <path d="m11.75 3v8.25c-.58.53-1.09 1.14-1.5 1.82v-10.07c0-.41.34-.75.75-.75s.75.34.75.75z" />
                      </svg>
                      <Trans i18nKey="messages.selfSellingGuide">Self-selling guide</Trans>
                    </button>
                    <button
                      className="conversation__menu-item"
                      onClick={() => { setShowHelpMenu(false); onShowTemplate?.(); }}
                    >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.5 1.5H4.5C3.67157 1.5 3 2.17157 3 3V15C3 15.8284 3.67157 16.5 4.5 16.5H13.5C14.3284 16.5 15 15.8284 15 15V6L10.5 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10.5 1.5V6H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 13.5V9M9 9L6.75 11.25M9 9L11.25 11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <Trans i18nKey="messages.loadTemplate">Deed of sale template</Trans>
                    </button>
                    {onShowExpertHelp && (
                    <button
                      className="conversation__menu-item"
                      onClick={() => { setShowHelpMenu(false); onShowExpertHelp(); }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <Trans i18nKey="messages.getPersonalHelp">Get personal help</Trans>
                    </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="conversation__messages" ref={messagesContainerRef}>
        {isLoading ? (
          <div className="conversation__loading">
            <div className="conversation__loading-spinner"></div>
          </div>
        ) : (
          <>
            {/* Welcome message when there are no messages to display */}
            {!isJaakkoConversation && (
            <div className="conversation__welcome-message">
              <div className="conversation__welcome-icon">
                {/*                   <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="presentation" focusable="false" style={{ display: 'block', height: '32px', width: '32px', fill: 'currentcolor' }}>
                    <path d="M16 0c8.833 0 16 7.167 16 16s-7.167 16-16 16S0 24.833 0 16 7.167 0 16 0zm0 1.5C7.992 1.5 1.5 7.992 1.5 16S7.992 30.5 16 30.5 30.5 24.008 30.5 16 24.008 1.5 16 1.5zm0 10.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5zm-6.5 0c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5zm13 0c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5zm-6.5 9c4.694 0 8.5 1.343 8.5 3s-3.806 3-8.5 3-8.5-1.343-8.5-3 3.806-3 8.5-3z"></path>
                  </svg> */}
              </div>
              <h4 className="conversation__welcome-title">
                <Trans i18nKey="messages.welcomeTitle" />
              </h4>
              <p className="conversation__welcome-text">
                <Trans i18nKey="messages.welcomeText" />
              </p>
              <div className="conversation__welcome-tips">
                <p>
                  <Trans i18nKey="messages.welcomeTips" />
                </p>
                <ul>
                  <li>
                    <Trans i18nKey="messages.tipPolite" />
                  </li>
                  <li>
                    <Trans i18nKey="messages.tipCaution" />
                  </li>
                  <li>
                    <Trans i18nKey="messages.tipDetails" />
                  </li>
                </ul>
              </div>
            </div>
            )}

            {/* Show the other party's off-market willingness event if available */}
            {/* Seller sees buyer's listing, Buyer sees seller's listing */}
            {isLoadingWillingness && (
              <div className="conversation__willingness-loading">
                <LoadingSpinner />
              </div>
            )}
            {otherPartyWillingness && !isLoadingWillingness && (
              // Check if the listing is still active (could_sell for sell listings, could_buy for buy listings)
              (otherPartyWillingness.could_sell || otherPartyWillingness.could_buy) ? (
                <div className="conversation__sell-willingness">
                  {otherPartyWillingness.could_sell ? (
                    <ListingCard
                      type="seller"
                      size="big"
                      listing={otherPartyListing as ListingCardListing}
                      onOpen={openListing}
                      newThresholdDays={0}
                    />
                  ) : (
                    <BuyerCard
                      size="big"
                      listing={otherPartyListing as ListingCardListing}
                      onOpen={openListing}
                      hideFooterActions
                    />
                  )}
                </div>
              ) : (
                <div className="conversation__listing-removed">
                  <div className="conversation__listing-removed__icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 2L22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="conversation__listing-removed__content">
                    <span className="conversation__listing-removed__title">
                      <Trans i18nKey={currentUserRole === 'SELLER' ? "messages.buyInterestRemoved.title" : "messages.listingRemoved.title"}>
                        {currentUserRole === 'SELLER' ? "Buy interest no longer available" : "Listing no longer available"}
                      </Trans>
                    </span>
                    <span className="conversation__listing-removed__description">
                      <Trans i18nKey={currentUserRole === 'SELLER' ? "messages.buyInterestRemoved.description" : "messages.listingRemoved.description"}>
                        {currentUserRole === 'SELLER'
                          ? "The buyer has removed their buy interest. You can still continue your conversation."
                          : "The seller has removed this listing. You can still continue your conversation."}
                      </Trans>
                    </span>
                  </div>
                </div>
              )
            )}

            {/* JAA-323: only where a usable composer exists. Jaakko/tip
                conversations hide the composer entirely, and ended chats
                disable it, so the bubbles would fill a box nobody can send.
                JAA-355 review: and only on an untouched thread; once either
                side has written, the first-message bubbles are gone. */}
            {!isJaakkoConversation && !conversation.isEnded && !thisUserHasSentMessage && !otherPartyHasWritten && (
              <FirstMessageSuggestions
                conversationId={conversationId}
                address={shortAddress}
                viewerRole={currentUserRole}
                value={newMessage}
                onPick={(text) => setNewMessage(text)}
              />
            )}

            {/* Display messages grouped by date */}
            {(() => {
              try {
                return Object.keys(groupedMessages).map(date => (
                  <div key={date} className="conversation__message-group">
                    <div className="conversation__date-separator">
                      <span>{formatDate(date)}</span>
                    </div>

                    {groupedMessages[date].map((message: any) => {
                      // System message — centered plain text
                      if (message.sender === 'system') {
                        return (
                          <div key={message.id} className="conversation__system-message">
                            <span>{message.text === 'EXPERT_HELP_REQUESTED'
                              ? <Trans i18nKey="messages.expertHelp.systemMessage">Expert help requested. We will contact you by phone or email.</Trans>
                              : message.systemEvent ? (() => {
                              const resolveLabel = (label?: { type: string; name?: string }) => {
                                if (!label) return t('messages.someone');
                                switch (label.type) {
                                  case 'self': return t('messages.you');
                                  case 'buyer': return t('messages.buyer');
                                  case 'seller': return t('messages.seller');
                                  case 'buyer_participant': return t('messages.buyerParticipant');
                                  case 'seller_participant': return t('messages.sellerParticipant');
                                  case 'participant': return t('messages.participants.participant');
                                  case 'pending_invitee': return t('messages.pendingInvitee');
                                  case 'revealed': return label.name || t('messages.someone');
                                  default: return t('messages.someone');
                                }
                              };
                              const eventType = message.systemEvent.type;
                              const actorLabel = message.systemEvent.actorLabel;
                              const targetLabel = message.systemEvent.targetLabel;

                              // participant_added: "User joined" (simplified, no names)
                              if (eventType === 'participant_added') {
                                if (targetLabel?.type === 'self') {
                                  return t('messages.system.youJoined');
                                }
                                return t('messages.system.participantJoined');
                              }
                              // participant_removed: keep actor/target labels
                              if (eventType === 'participant_removed' && targetLabel?.type === 'self') {
                                return t('messages.system.youWereRemoved', {
                                  actor: resolveLabel(actorLabel)
                                });
                              }
                              if (eventType === 'participant_removed' && actorLabel?.type === 'self') {
                                return t('messages.system.youRemovedParticipant', {
                                  target: resolveLabel(targetLabel)
                                });
                              }
                              // invite_sent: use role for actor, no target name
                              if (eventType === 'invite_sent') {
                                if (actorLabel?.type === 'self') {
                                  return t('messages.system.youInvited');
                                }
                                if (targetLabel?.type === 'self') {
                                  return t('messages.system.youWereInvited', {
                                    actor: resolveLabel(actorLabel)
                                  });
                                }
                                return t('messages.system.inviteSent', {
                                  actor: resolveLabel(actorLabel)
                                });
                              }

                              return t(`messages.system.${
                                eventType === 'participant_removed' ? 'participantRemoved' :
                                eventType === 'participant_left' ? 'participantLeft' :
                                eventType === 'role_changed' ? 'roleChanged' : 'participantAdded'
                              }`, {
                                actor: resolveLabel(actorLabel),
                                target: resolveLabel(targetLabel),
                                role: message.systemEvent.newRole || message.systemEvent.role
                              });
                            })() : message.text}</span>
                          </div>
                        );
                      }

                      return (
                      <div
                        key={message.id}
                        className={`conversation__message conversation__message--${message.sender}${message.sender === 'jaakko' ? ' conversation__message--jaakko' : ''}`}
                      >
                        {message.sender === 'other' && (
                          <div className="conversation__message-avatar">
                            <ChatUserAvatar
                              name={message.senderName && message.senderName !== 'Unknown' ? message.senderName : ""}
                              height={28}
                              width={28}
                              verified={
                                !!participants.find(p => p.userId === message.senderId)?.verified
                              }
                              badgeSize="sm"
                            />
                          </div>
                        )}
                        {message.sender === 'jaakko' && (
                          <div className="conversation__message-avatar conversation__message-avatar--jaakko">
                            <img src="/assets/jaakko_head.png" alt="Jaakko" />
                          </div>
                        )}
                        <div className="conversation__message-content">
                          {/* Tip message with embedded listing */}
                          {message.tipData && message.tipData.type === 'SELL_LISTING_TIP' ? (
                            <div className="conversation__tip-message">
                              <div className="conversation__tip-message__text">
                                <Trans i18nKey="tipBuyer.receivedMessage">A seller tipped you about this property:</Trans>
                              </div>
                              <a
                                href={listingPath(
                                  message.tipData.sell_willingness_id,
                                  message.tipData.address,
                                )}
                                className="conversation__tip-message__listing"
                                onClick={(e) => {
                                  e.preventDefault();
                                  openListing({
                                    _id: message.tipData.sell_willingness_id,
                                    address: message.tipData.address,
                                  });
                                }}
                              >
                                {message.tipData.images && message.tipData.images.length > 0 && (
                                  <img
                                    src={message.tipData.images[0].thumbnailUrl || message.tipData.images[0].url}
                                    alt=""
                                    className="conversation__tip-message__listing__image"
                                  />
                                )}
                                <div className="conversation__tip-message__listing__info">
                                  <div className="conversation__tip-message__listing__address">
                                    {message.tipData.address_string}
                                  </div>
                                  <div className="conversation__tip-message__listing__details">
                                    {message.tipData.property_rooms && (
                                      <span>{message.tipData.property_rooms} {t('building.offMarket.modal.rooms', { count: message.tipData.property_rooms })}</span>
                                    )}
                                    {message.tipData.property_size && (
                                      <span>{message.tipData.property_size} m²</span>
                                    )}
                                    {message.tipData.property_asking_price && (
                                      <span>{formatCardPrice(message.tipData.property_asking_price)}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="conversation__tip-message__listing__arrow">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </div>
                              </a>
                            </div>
                          ) : (
                            <>
                              {(message.text && message.text !== 'TIP_MESSAGE') && (
                                <div className="conversation__message-bubble">
                                  {message.text && (
                                    <Linkify
                                      options={{
                                        attributes: {
                                          rel: 'noopener noreferrer',
                                          target: '_blank',
                                        },
                                      }}
                                    >
                                      {DOMPurify.sanitize(message.text)}
                                    </Linkify>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {message.attachments && message.attachments.length > 0 && (
                            <MessageAttachments
                              attachments={message.attachments}
                              sender={message.sender}
                            />
                          )}
                          <div className="conversation__message-time">
                            {formatMessageTime(message.timestamp)}
                            {message.id === readStatus?.messageId && (
                              <span className="conversation__message-status">
                                {' '}
                                <span aria-hidden="true">·</span>
                                {' '}
                                {readStatus.seen ? t('messages.readStatus.seen') : t('messages.readStatus.sent')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ));
              } catch (error) {
                console.error('Error rendering messages:', error);
                return (
                  <div className="conversation__error-message">
                    <p>There was an error displaying messages. Please try refreshing the page.</p>
                  </div>
                );
              }
            })()}

            {/* Show ended conversation message if the conversation is ended */}
            {conversation.isEnded && (
              <div className="conversation__ended-message">
                <p>
                  <Trans i18nKey="messages.chatEndedMessage">
                    This conversation has been ended. No new messages can be sent.
                  </Trans>
                </p>
                {conversation.endedAt && (
                  <span className="conversation__ended-date">
                    {formatEndedDate(conversation.endedAt)}
                  </span>
                )}
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
      {!isJaakkoConversation && (
      <div className={`conversation__input ${conversation.isEnded ? 'conversation__input--disabled' : ''}`}>
        {!conversation.isEnded && (
          <AttachmentPicker
            attachments={attachments}
            onAddAttachments={handleAddAttachments}
            onRemoveAttachment={handleRemoveAttachment}
            fileInputRef={fileInputRef}
            disabled={conversation.isEnded || isUploading}
          />
        )}
        {overLimitBy > 0 ? (
          <p
            className="conversation__composer-hint conversation__composer-hint--error"
            role="status"
          >
            {messageTooLongText(t, i18n.language, overLimitBy)}
          </p>
        ) : (
          composerHint && (
            <p className="conversation__composer-hint" role="status">
              {composerHint}
            </p>
          )
        )}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={conversation.isEnded ? t('messages.chatEndedPlaceholder') : (isMobile ? t('messages.writePlaceholderMobile') : t('messages.writePlaceholder'))}
            rows={3}
            disabled={conversation.isEnded || isUploading}
          />
          <div
            className="conversation__input-buttons"
          >
            <button
              className="attachment-picker__button"
              onClick={() => fileInputRef.current?.click()}
              disabled={conversation.isEnded || isUploading || isSignedOutDraft}
              aria-label="Add attachments"
              // Hidden, not removed, while signed out: its slot keeps Send from moving when login brings it back.
              style={isSignedOutDraft ? { visibility: 'hidden' } : undefined}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <button
              className="conversation__send-button"
              onClick={handleSendMessage}
              disabled={
                (!newMessage.trim() && attachments.length === 0) ||
                overLimitBy > 0 ||
                conversation.isEnded ||
                isUploading
              }
            >
              {isUploading ? (
                  <LoadingSpinner />
              ) : (
                <Trans i18nKey="messages.send">Send</Trans>
              )}
            </button>
          </div>
        </div>
      </div>
      )}
      {showParticipantPanel && (
        <ParticipantPanel
          conversationId={conversationId}
          participants={participants}
          pendingInviteEmail={conversation.pendingInviteEmail}
          currentUserRole={currentUserParticipantRole}
          canInvite={conversation.canInvite}
          onClose={() => setShowParticipantPanel(false)}
          onInvite={() => {
            setShowParticipantPanel(false);
            setShowInviteModal(true);
          }}
          onLeave={() => {
            setShowParticipantPanel(false);
            goBack();
          }}
          refreshConversations={refreshConversations}
          onRefreshMessages={onRefreshMessages}
        />
      )}
      {showInviteModal && (
        <InviteParticipantModal
          conversationId={conversationId}
          pendingInviteEmail={conversation.pendingInviteEmail}
          onClose={() => setShowInviteModal(false)}
          refreshConversations={refreshConversations}
          onRefreshMessages={onRefreshMessages}
        />
      )}
    </div>
  );
};

export default Conversation;
